import React, { useMemo } from "react";
import { addDays, format } from "date-fns";
import {
    Button,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Box, Typography,
} from "@mui/material";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { useTranslation } from "react-i18next";
import {Link} from "react-router-dom";
import {formatDate} from "../../common/DateUtil";

// Visible window: 08:00 -> 17:00
const START_HOUR = 8;
const END_HOUR = 17; // end-exclusive

// 30-min header slots (keep your header)
const START_INDEX = START_HOUR * 2; // each slot = 30 minutes
const END_INDEX = END_HOUR * 2; // end-exclusive index
const VISIBLE_SLOTS = END_INDEX - START_INDEX; // 18 slots (08:00..16:30)
const SLOT_BORDER_COLOR = "lightgrey";

// Minute-based rendering (best fix)
const VISIBLE_START_MIN = START_HOUR * 60;
const VISIBLE_END_MIN = END_HOUR * 60;
const VISIBLE_MINUTES = VISIBLE_END_MIN - VISIBLE_START_MIN;

// Layout
const LANE_HEIGHT = 28; // px
const LANE_GAP = 6; // px
const BAR_RADIUS = 4;

const SLOTS = Array.from({ length: VISIBLE_SLOTS }, (_, i) => {
  const globalIndex = START_INDEX + i;
  const h = Math.floor(globalIndex / 2);
  const m = globalIndex % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
});

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function toMin(date) {
  return date.getHours() * 60 + date.getMinutes();
}

/**
 * Clip an appointment to the visible window and compute:
 * - startMin/endMin inside window
 * - left%/width% for rendering
 */
function calcBar(startAt, endAt) {
  const sRaw = toMin(startAt);
  const eRaw = toMin(endAt);

  // Clip to window bounds
  const s = clamp(sRaw, VISIBLE_START_MIN, VISIBLE_END_MIN);
  const e = clamp(eRaw, VISIBLE_START_MIN, VISIBLE_END_MIN);

  const duration = e - s;
  if (duration <= 0) return null;

  const leftPct = ((s - VISIBLE_START_MIN) / VISIBLE_MINUTES) * 100;
  const widthPct = (duration / VISIBLE_MINUTES) * 100;

  return { startMin: s, endMin: e, leftPct, widthPct };
}

/**
 * Assign overlapping appointments into vertical "lanes" (stacking) per service row.
 * Greedy: sort by start, place into first lane whose lastEnd <= start; else create new lane.
 */
function assignLanes(bars) {
  const sorted = [...bars].sort((a, b) => {
    if (a.startMin !== b.startMin) return a.startMin - b.startMin;
    return a.endMin - b.endMin;
  });

  const lanesEnd = []; // last endMin per lane
  const out = [];

  for (const b of sorted) {
    let lane = -1;
    for (let i = 0; i < lanesEnd.length; i += 1) {
      if (lanesEnd[i] <= b.startMin) {
        lane = i;
        break;
      }
    }
    if (lane === -1) {
      lane = lanesEnd.length;
      lanesEnd.push(b.endMin);
    } else {
      lanesEnd[lane] = b.endMin;
    }
    out.push({ ...b, lane });
  }

  return { items: out, laneCount: lanesEnd.length || 1 };
}

/**
 * Optional: stable color per service (simple hash)
 */
function colorForService(name) {
  // nice default teal if you don’t care:
  return "#00acc1";

  // // deterministic-ish palette via HSL
  // let hash = 0;
  // for (let i = 0; i < name.length; i += 1) {
  //     hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  // }
  // const hue = hash % 360;
  // return `hsl(${hue} 70% 40%)`;
}

export default function AppointmentCalendar({
  services,
  appointments,
  date,
  setDate,
}) {
  const { t } = useTranslation();

  const items = useMemo(() => {
    return (appointments || [])
      .map((appointment, idx) => {
        const startAt = appointment.startAt
          ? new Date(appointment.startAt)
          : appointment.start || appointment.startTime
            ? new Date(appointment.start || appointment.startTime)
            : null;

        const endAt = appointment.endAt
          ? new Date(appointment.endAt)
          : appointment.end || appointment.endTime
            ? new Date(appointment.end || appointment.endTime)
            : null;

        return {
          key: appointment.uuid || appointment.id || idx,
          startAt,
          endAt,
          animalName: appointment.animalName,
            animalUuid: appointment.animalUuid,
          serviceName:
            appointment.service?.name ||
            appointment.serviceName ||
            appointment.service ||
            "-",
          customerName:
            appointment.customer?.name ||
            appointment.customerName ||
            appointment.customer ||
            "",
        };
      })
      .filter((it) => it.startAt && it.endAt);
  }, [appointments]);

  // Group by service name for rows (and compute minute-accurate bars + lanes)
  const rows = useMemo(() => {
    const map = new Map();

    // Initialize rows for all services (keeps empty services visible)
    const allServices = (services || []).map((s) => s.serviceName);
    allServices.forEach((name) => map.set(name, []));

    // Push items into correct service bucket
    items.forEach((it) => {
      const key = it.serviceName || "-";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    });

    // For each service, convert to bars (clipped) + lane assignment
    return Array.from(map.entries()).map(([serviceName, list]) => {
      const bars = (list || [])
        .map((it) => {
          const bar = calcBar(it.startAt, it.endAt);
          if (!bar) return null;
          return { ...it, ...bar };
        })
        .filter(Boolean);

      const { items: laneItems, laneCount } = assignLanes(bars);

      return {
        serviceName,
        laneItems, // items with leftPct/widthPct + lane
        laneCount, // number of vertical stacks needed
      };
    });
  }, [items, services]);

  const handleDayRefresh = (delta) => setDate(addDays(date, delta));

  const monthLabel = date.toLocaleString("en", { month: "long" }).toLowerCase();

  return (
    <>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<KeyboardDoubleArrowLeftIcon />}
            color="primary"
            onClick={() => handleDayRefresh(-1)}
            data-testid="prev-day-button"
          />
          <Button variant="outlined" data-testid="current-date-display">
            {`${t(monthLabel)} ${format(date, "dd")}, ${format(date, "yyyy")}`}
          </Button>
          <Button
            startIcon={<KeyboardDoubleArrowRightIcon />}
            variant="contained"
            color="primary"
            onClick={() => handleDayRefresh(1)}
            data-testid="next-day-button"
          />
        </Stack>
      </Grid>

      <TableContainer component={Paper}>
        <Table
          size="small"
          sx={{ tableLayout: "fixed", borderCollapse: "collapse" }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 200, fontWeight: 600 }}>
                {t("service") || "Service"}
              </TableCell>

              <TableCell
                colSpan={VISIBLE_SLOTS}
                sx={{
                  p: 0,
                  position: "relative",
                  borderLeft: `1px solid ${SLOT_BORDER_COLOR}`,
                  borderRight: `1px solid ${SLOT_BORDER_COLOR}`,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    // header height (tweak if you want)
                    height: 36,
                    display: "grid",
                    gridTemplateColumns: `repeat(${VISIBLE_SLOTS}, 1fr)`,
                    alignItems: "center",
                    // SAME grid lines as body:
                    backgroundImage: `repeating-linear-gradient(
            to right,
            transparent 0,
            transparent calc((100% / ${VISIBLE_SLOTS}) - 1px),
            ${SLOT_BORDER_COLOR} calc((100% / ${VISIBLE_SLOTS}) - 1px),
            ${SLOT_BORDER_COLOR} calc(100% / ${VISIBLE_SLOTS})
          )`,
                  }}
                >
                  {SLOTS.map((s) => (
                    <Box
                      key={s}
                      sx={{
                        textAlign: "center",
                        fontSize: 12,
                        fontWeight: 600,
                        // padding only for text; doesn't affect width math
                        px: 0.5,
                        userSelect: "none",
                      }}
                    >
                      {s}
                    </Box>
                  ))}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => {
              const rowHeight =
                row.laneCount * LANE_HEIGHT + (row.laneCount - 1) * LANE_GAP;

              return (
                <TableRow
                  key={row.serviceName}
                  sx={{
                    height: rowHeight,
                    "& > td": {
                      py: 0, // remove default vertical padding
                      verticalAlign: "top",
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 600, verticalAlign: "top" }}>
                    {row.serviceName}
                  </TableCell>

                  {/* One big timeline cell spanning the visible window */}
                  <TableCell
                    colSpan={VISIBLE_SLOTS}
                    sx={{
                      p: 0, // important
                      borderLeft: `1px solid ${SLOT_BORDER_COLOR}`,
                      borderRight: `1px solid ${SLOT_BORDER_COLOR}`,
                      position: "relative",
                      verticalAlign: "top",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: Math.max(rowHeight, LANE_HEIGHT),
                        backgroundImage: `repeating-linear-gradient(
        to right,
        transparent 0,
        transparent calc((100% / ${VISIBLE_SLOTS}) - 1px),
        ${SLOT_BORDER_COLOR} calc((100% / ${VISIBLE_SLOTS}) - 1px),
        ${SLOT_BORDER_COLOR} calc(100% / ${VISIBLE_SLOTS})
      )`,
                        backgroundSize: "100% 100%",
                        // optional: add some inner padding for bars without affecting grid math
                        // padding: '4px',
                      }}
                    >
                      {row.laneItems.map((appt) => {
                        const top = appt.lane * (LANE_HEIGHT + LANE_GAP);
                        const bg = colorForService(row.serviceName);

                        return (
                            <Tooltip
                                key={appt.id}
                                title={
                                    <div style={{ padding: 8 }}>
                                        <Typography
                                            color="inherit"
                                            variant="subtitle2"
                                            sx={{ fontWeight: 600, lineHeight: 1.25 }}
                                        >
                                            <Link
                                                to={`/beheer/dieren/${appt.animalUuid}`}
                                                style={{
                                                    color: "inherit",
                                                    textDecoration: "underline",
                                                }}
                                            >
                                                {t("appointmentFor")}{" "}
                                                {appt.animalName}
                                            </Link>
                                        </Typography>
                                        <div
                                            style={{
                                                borderTop: "1px solid #eceff1",
                                                margin: "6px 0",
                                            }}
                                        />
                                        <Typography color="inherit" variant="body2">
                                            <strong>{t("check_in")}</strong>{" "}
                                            {`${format(appt.startAt, "HH:mm")}h`}
                                        </Typography>
                                        <Typography color="inherit" variant="body2">
                                            <strong>{t("check_out")}</strong>{" "}
                                            {`${format(appt.endAt, "HH:mm")}h`}
                                        </Typography>
                                    </div>
                                }
                                placement="top"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: "#fff",
                                            color: "rgba(0,0,0,0.87)",
                                            border: "1px solid #dadde9",
                                            boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
                                            borderRadius: 1,
                                            maxWidth: 420,
                                            fontSize: 12,
                                            p: 0,
                                        },
                                    },
                                    popper: {
                                        modifiers: [
                                            { name: "offset", options: { offset: [0, -15] } },
                                        ],
                                    },
                                }}
                            >
                                <div
                                    style={{
                                        position: "absolute",
                                        left: `${appt.leftPct}%`,
                                        width: `${appt.widthPct}%`,
                                        top: top + LANE_HEIGHT / 2,
                                        height: LANE_HEIGHT - 6,
                                        transform: "translateY(-50%)",
                                        backgroundColor: "#00acc1",
                                        color: "white",
                                        padding: "4px 6px",
                                        fontSize: "0.75rem",
                                        borderRadius: 4,
                                        display: "flex",
                                        alignItems: "center",
                                        boxSizing: "border-box",
                                        zIndex: 3,
                                        pointerEvents: "auto", // hover/tooltip actief
                                        willChange: "transform",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    data-testid={`calendar-appointment-${appt.animalName}`}
                                >
                                    {appt.animalName}
                                </div>
                            </Tooltip>
                        );
                      })}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
